// Theme utilities: hex→HSL conversion (because shadcn's CSS vars are HSL), the
// preset swatches surfaced in Settings → Appearance, and the applyTheme helper
// that pushes a user's preferences onto :root via CSS custom properties.

export type LayoutDensity = 'minimalist' | 'comfortable' | 'compact';

export interface ThemePreferences {
  theme_background?: string | null;
  theme_accent?: string | null;
  theme_card?: string | null;
  theme_layout?: LayoutDensity | null;
  theme_font_scale?: number | null;
  theme_reading_width?: number | null;
}

export interface Swatch {
  id: string;
  name: string;
  hex: string;
}

export interface ThemePreset {
  id: string;
  name: string;
  description: string;
  background: string;
  accent: string;
  card: string;
}

// ─── Named theme presets ───────────────────────────────────────────────────
// One-click palettes that set background + accent + card simultaneously.
export const THEME_PRESETS: ThemePreset[] = [
  {
    id: 'default',
    name: 'Default',
    description: 'Clean white with deep navy accent.',
    background: '#FFFFFF',
    accent: '#1A2E4A',
    card: '#FFFFFF',
  },
  {
    id: 'paper',
    name: 'Paper',
    description: 'Warm cream — easy on the eyes for long reads.',
    background: '#FAF7EF',
    accent: '#5C4033',
    card: '#FFFEF9',
  },
  {
    id: 'deep-navy',
    name: 'Deep Navy',
    description: 'Dark blue — calming focus mode.',
    background: '#0E1D2F',
    accent: '#4A9EF5',
    card: '#152440',
  },
  {
    id: 'forest',
    name: 'Forest',
    description: 'Earthy greens for a grounded reading session.',
    background: '#1C2B1A',
    accent: '#6DB86D',
    card: '#243622',
  },
  {
    id: 'midnight',
    name: 'Midnight',
    description: 'Pure dark with a soft purple accent.',
    background: '#0A0A0A',
    accent: '#8A6FE8',
    card: '#141414',
  },
];

// Shared starter palette across all three pickers (background / accent / card).
export const STARTER_SWATCHES: Swatch[] = [
  { id: 'navy',         name: 'Navy',           hex: '#1A2E4A' },
  { id: 'charcoal',     name: 'Charcoal',       hex: '#36393F' },
  { id: 'warm-white',   name: 'Warm white',     hex: '#FAF7EF' },
  { id: 'soft-pink',    name: 'Soft pink',      hex: '#F9E4E4' },
  { id: 'dusty-rose',   name: 'Dusty rose',     hex: '#E8B4B8' },
  { id: 'sage-green',   name: 'Sage green',     hex: '#B2C9B2' },
  { id: 'lavender',     name: 'Lavender',       hex: '#D4C5E2' },
  { id: 'midnight',     name: 'Midnight black', hex: '#0A0A0A' },
];

export const DEFAULT_ACCENT_HEX = '#1A2E4A';
export const DEFAULT_BACKGROUND_HEX = '#FFFFFF';
export const DEFAULT_CARD_HEX = '#FFFFFF';
export const DEFAULT_LAYOUT: LayoutDensity = 'minimalist';
export const DEFAULT_FONT_SCALE = 1.0;
export const DEFAULT_READING_WIDTH = 768;

export function isValidHex(input: string): boolean {
  return /^#?[0-9a-fA-F]{3}([0-9a-fA-F]{3})?$/.test(input.trim());
}

export function normalizeHex(input: string): string {
  const t = input.trim();
  return t.startsWith('#') ? t : `#${t}`;
}

interface HSL {
  h: number;
  s: number;
  l: number;
}

export function hexToHsl(hex: string): HSL | null {
  const m = hex.replace(/^#/, '').match(/^([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/);
  if (!m) return null;
  let r: number, g: number, b: number;
  const v = m[1];
  if (v.length === 3) {
    r = parseInt(v[0] + v[0], 16);
    g = parseInt(v[1] + v[1], 16);
    b = parseInt(v[2] + v[2], 16);
  } else {
    r = parseInt(v.slice(0, 2), 16);
    g = parseInt(v.slice(2, 4), 16);
    b = parseInt(v.slice(4, 6), 16);
  }
  const rf = r / 255, gf = g / 255, bf = b / 255;
  const max = Math.max(rf, gf, bf);
  const min = Math.min(rf, gf, bf);
  const l = (max + min) / 2;
  let h = 0, s = 0;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case rf: h = (gf - bf) / d + (gf < bf ? 6 : 0); break;
      case gf: h = (bf - rf) / d + 2; break;
      case bf: h = (rf - gf) / d + 4; break;
    }
    h /= 6;
  }
  return {
    h: Math.round(h * 360),
    s: Math.round(s * 100),
    l: Math.round(l * 100),
  };
}

export function hslVar(hsl: HSL): string {
  return `${hsl.h} ${hsl.s}% ${hsl.l}%`;
}

// Sets CSS custom properties on :root so every component picks up the change
// without rerendering. Falls back to the project defaults when a slot is null.
export function applyTheme(prefs: ThemePreferences) {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;

  const bgHex = prefs.theme_background || DEFAULT_BACKGROUND_HEX;
  const bgHsl = hexToHsl(bgHex);
  if (bgHsl) {
    root.style.setProperty('--background', hslVar(bgHsl));
    const fg = bgHsl.l > 55 ? '0 0% 10%' : '0 0% 96%';
    root.style.setProperty('--foreground', fg);
    const muted = bgHsl.l > 55
      ? `${bgHsl.h} ${bgHsl.s}% ${Math.max(bgHsl.l - 6, 0)}%`
      : `${bgHsl.h} ${bgHsl.s}% ${Math.min(bgHsl.l + 8, 100)}%`;
    root.style.setProperty('--muted', muted);
    root.style.setProperty('--secondary', muted);
    root.style.setProperty('--accent', muted);
    const border = bgHsl.l > 55
      ? `${bgHsl.h} ${bgHsl.s}% ${Math.max(bgHsl.l - 12, 0)}%`
      : `${bgHsl.h} ${bgHsl.s}% ${Math.min(bgHsl.l + 14, 100)}%`;
    root.style.setProperty('--border', border);
    root.style.setProperty('--input', border);
  }

  const cardHex = prefs.theme_card || prefs.theme_background || DEFAULT_CARD_HEX;
  const cardHsl = hexToHsl(cardHex);
  if (cardHsl) {
    root.style.setProperty('--card', hslVar(cardHsl));
    root.style.setProperty('--popover', hslVar(cardHsl));
    const cardFg = cardHsl.l > 55 ? '0 0% 10%' : '0 0% 96%';
    root.style.setProperty('--card-foreground', cardFg);
    root.style.setProperty('--popover-foreground', cardFg);
  }

  const accentHex = prefs.theme_accent || DEFAULT_ACCENT_HEX;
  const accentHsl = hexToHsl(accentHex);
  if (accentHsl) {
    root.style.setProperty('--primary', hslVar(accentHsl));
    root.style.setProperty('--ring', hslVar(accentHsl));
    root.style.setProperty('--sidebar-primary', hslVar(accentHsl));
    const onPrimary = accentHsl.l > 55 ? '0 0% 8%' : '0 0% 98%';
    root.style.setProperty('--primary-foreground', onPrimary);
    root.style.setProperty('--sidebar-primary-foreground', onPrimary);
  }

  const layout: LayoutDensity = prefs.theme_layout || DEFAULT_LAYOUT;
  root.setAttribute('data-density', layout);

  // Font scale: scales the root font size so all rem-based text resizes proportionally.
  const scale = prefs.theme_font_scale ?? DEFAULT_FONT_SCALE;
  root.style.fontSize = `${scale * 16}px`;
  root.style.setProperty('--font-scale', String(scale));

  // Reading width: constrains the main content column via a CSS variable.
  const rw = prefs.theme_reading_width ?? DEFAULT_READING_WIDTH;
  root.style.setProperty('--reading-width', `${rw}px`);
  root.setAttribute('data-reading-width', String(rw));
}
